import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/api";
import Navigation from "../components/Navigation";
import ChangePasswordModal from "../components/ChangePasswordModal";
import "../styles/test-system.css";
import "./Profile.css";
import { calendarDateValue, formatCalendarDate, formatToLocalDate } from "../utils/dateUtils";
import { isAuthenticated, clearAuthData } from "../utils/auth";
import COUNTRIES from "../utils/countries";

/*
 * The candidate's own account page.
 *
 * It is drawn on the same surface as every other Appli-level page — the `ts-`
 * primitives and `styles/tokens.css` — because it used to be somewhere else
 * entirely: white cards on a white page, a bright blue avatar disc that appears
 * nowhere else in the product, two equally black buttons competing to be the
 * primary one, and a stylesheet injected into `document.head` at module load.
 * Nothing here is a new visual language; it is this page joining the one the
 * rest of the portal already speaks.
 */

/* Stored lowercase at sign-up (`Signup.js` lowercases before it posts), and read
   back raw — which printed "canada" under a label. The canonical spelling comes
   from the same list the sign-up offers, so "bosnia and herzegovina" comes back
   as the country and not as Title Case Applied To Every Word. */
const COUNTRY_BY_KEY = COUNTRIES.reduce((index, name) => {
    index[name.toLowerCase()] = name;
    return index;
}, {});

const countryLabel = (value) => {
    const name = (value || "").trim();
    if (!name) return "";
    return COUNTRY_BY_KEY[name.toLowerCase()]
        || name.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/** One label / value pair, read-only. Empty reads as empty, not as a gap. */
const Fact = ({ label, children, empty = "Not provided" }) => (
    <div className="ts-fact">
        <dt>{label}</dt>
        <dd>{children || <span className="profile-unset">{empty}</span>}</dd>
    </div>
);

/** One labelled control. The label is a real one: `<input type="date">` shows no
    placeholder, so a placeholder-only field has no name at all. */
const Field = ({ id, label, children, help }) => (
    <div className="profile-field">
        <label className="profile-field-label" htmlFor={id}>{label}</label>
        {children}
        {help && <p className="profile-field-help">{help}</p>}
    </div>
);

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedUser, setEditedUser] = useState({});
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const loadAccountData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!isAuthenticated()) {
                navigate("/login");
                return;
            }

            const response = await API.get("users/account-data");

            setUser(response.data.user);
            setEditedUser(response.data.user);
        } catch (err) {
            console.error("Error loading account data:", err);
            if (err.response?.status === 401) {
                clearAuthData();
                navigate("/login");
            } else {
                setError("We could not load your account. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadAccountData();
    }, [loadAccountData]);

    const handleEdit = () => {
        setIsEditing(true);
        setSuccessMessage("");
        setEditedUser({ ...user });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError(null);
        setEditedUser({ ...user });
    };

    const handleInputChange = (field, value) => {
        setEditedUser((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            if (!isAuthenticated()) {
                navigate("/login");
                return;
            }

            const updateData = {};

            if (editedUser.first_name !== user.first_name) {
                updateData.firstname = editedUser.first_name;
            }
            if (editedUser.last_name !== user.last_name) {
                updateData.lastname = editedUser.last_name;
            }
            /* Both sides as a calendar date: the stored value arrives as an instant
               ("2000-05-14 00:00:00+00:00") and the field holds "2000-05-14", so
               comparing the two raw strings called every save a change. */
            if (calendarDateValue(editedUser.birth_date) !== calendarDateValue(user.birth_date)) {
                const birthDate = calendarDateValue(editedUser.birth_date);
                if (birthDate) {
                    updateData.birth_date = birthDate;
                }
            }
            if (editedUser.country !== user.country) {
                updateData.country = editedUser.country;
            }
            if (editedUser.linkedin_profile !== user.linkedin_profile) {
                updateData.linkedin_profile = editedUser.linkedin_profile || "";
            }
            if (editedUser.twitter_handle !== user.twitter_handle) {
                updateData.twitter_handle = editedUser.twitter_handle || "";
            }

            if (Object.keys(updateData).length === 0) {
                setIsEditing(false);
                return;
            }

            const response = await API.post("users/update-account", updateData);

            if (response.data && response.data.user) {
                setUser(response.data.user);
                setEditedUser(response.data.user);
            } else {
                setUser(editedUser);
            }

            setIsEditing(false);
            setSuccessMessage("Your profile is saved.");
            setTimeout(() => setSuccessMessage(""), 4000);
        } catch (err) {
            console.error("Error saving account data:", err);

            if (err.response?.status === 401) {
                clearAuthData();
                navigate("/login");
            } else if (err.response?.data?.error) {
                setError(`We could not save your changes: ${err.response.data.error}`);
            } else {
                setError("We could not save your changes. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChangeSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => {
            setSuccessMessage("");
        }, 5000);
    };

    const fullName = useMemo(() => {
        if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`;
        return user?.username || "Your profile";
    }, [user]);

    if (loading) {
        return (
            <>
                <Navigation />
                <div className="ts-page">
                    <div className="ts-shell">
                        <div className="ts-page-state" role="status">
                            <span className="ts-spinner" aria-hidden="true" />
                            <p>Loading your profile…</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error && !user) {
        return (
            <>
                <Navigation />
                <div className="ts-page">
                    <div className="ts-shell">
                        <div className="ts-page-state">
                            <h2>Something went wrong</h2>
                            <p>{error}</p>
                            <button className="ts-btn" onClick={loadAccountData}>Try again</button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const monogram = (user?.first_name?.[0] || user?.username?.[0] || "?").toUpperCase();

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell">
                    <header className="ts-head">
                        <span className="ts-eyebrow">Appli</span>
                        <h1 className="ts-title">Your profile</h1>
                        <p className="ts-lede">
                            The details on your account. Your name is what the team and, on a
                            group cycle, your teammates see.
                        </p>
                    </header>

                    {successMessage && (
                        <p className="ts-note ts-note--ok profile-message" role="status">
                            <CheckIcon />
                            <span>{successMessage}</span>
                        </p>
                    )}

                    {error && user && (
                        <p className="ts-note ts-note--danger profile-message" role="alert">
                            <AlertIcon />
                            <span>{error}</span>
                        </p>
                    )}

                    <section className="ts-panel profile-identity">
                        <span className="profile-monogram" aria-hidden="true">{monogram}</span>
                        <div className="profile-identity-body">
                            <h2 className="profile-name">{fullName}</h2>
                            <p className="profile-email">{user?.email}</p>
                            {user?.registered_date && (
                                <p className="profile-since">
                                    Member since {formatToLocalDate(user.registered_date)}
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="ts-panel">
                        <div className="profile-panel-head">
                            <h2 className="ts-panel-title profile-panel-title">Your details</h2>
                            {!isEditing && (
                                <div className="profile-actions">
                                    <button className="ts-btn ts-btn--primary" onClick={handleEdit}>
                                        Edit details
                                    </button>
                                    <button
                                        className="ts-btn"
                                        onClick={() => setShowPasswordModal(true)}
                                    >
                                        Change password
                                    </button>
                                </div>
                            )}
                        </div>

                        {isEditing ? (
                            <form
                                className="profile-form"
                                onSubmit={(event) => { event.preventDefault(); handleSave(); }}
                            >
                                <div className="profile-fields">
                                    <Field id="profile-first-name" label="First name">
                                        <input
                                            id="profile-first-name"
                                            className="profile-input"
                                            type="text"
                                            value={editedUser.first_name || ""}
                                            onChange={(event) => handleInputChange("first_name", event.target.value)}
                                        />
                                    </Field>

                                    <Field id="profile-last-name" label="Last name">
                                        <input
                                            id="profile-last-name"
                                            className="profile-input"
                                            type="text"
                                            value={editedUser.last_name || ""}
                                            onChange={(event) => handleInputChange("last_name", event.target.value)}
                                        />
                                    </Field>

                                    <Field id="profile-birth-date" label="Date of birth">
                                        <input
                                            id="profile-birth-date"
                                            className="profile-input"
                                            type="date"
                                            value={calendarDateValue(editedUser.birth_date)}
                                            onChange={(event) => handleInputChange("birth_date", event.target.value)}
                                        />
                                    </Field>

                                    <Field id="profile-country" label="Country">
                                        <select
                                            id="profile-country"
                                            className="profile-input"
                                            value={countryLabel(editedUser.country)}
                                            onChange={(event) => handleInputChange(
                                                "country",
                                                event.target.value.toLowerCase()
                                            )}
                                        >
                                            <option value="">Select a country</option>
                                            {COUNTRIES.map((name) => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                    </Field>

                                    <Field
                                        id="profile-linkedin"
                                        label="LinkedIn profile"
                                        help="Optional — e.g. https://linkedin.com/in/yourprofile"
                                    >
                                        <input
                                            id="profile-linkedin"
                                            className="profile-input"
                                            type="url"
                                            placeholder="https://linkedin.com/in/username"
                                            value={editedUser.linkedin_profile || ""}
                                            onChange={(event) => handleInputChange("linkedin_profile", event.target.value)}
                                        />
                                    </Field>

                                    <Field id="profile-twitter" label="X / Twitter handle" help="Optional">
                                        <input
                                            id="profile-twitter"
                                            className="profile-input"
                                            type="text"
                                            placeholder="@username"
                                            value={editedUser.twitter_handle || ""}
                                            onChange={(event) => handleInputChange("twitter_handle", event.target.value)}
                                        />
                                    </Field>
                                </div>

                                <div className="profile-actions profile-actions--form">
                                    <button
                                        className="ts-btn ts-btn--primary"
                                        type="submit"
                                        disabled={saving}
                                    >
                                        {saving && <span className="ts-spinner-sm" />}
                                        {saving ? "Saving…" : "Save changes"}
                                    </button>
                                    <button className="ts-btn" type="button" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <dl className="ts-facts profile-facts">
                                <Fact label="Username">{user?.username}</Fact>
                                <Fact label="First name">{user?.first_name}</Fact>
                                <Fact label="Last name">{user?.last_name}</Fact>
                                <Fact label="Date of birth">
                                    {user?.birth_date ? formatCalendarDate(user.birth_date) : ""}
                                </Fact>
                                <Fact label="Country">{countryLabel(user?.country)}</Fact>
                                <Fact label="LinkedIn profile">
                                    {user?.linkedin_profile ? (
                                        <a
                                            className="ts-link"
                                            href={user.linkedin_profile}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {user.linkedin_profile}
                                        </a>
                                    ) : null}
                                </Fact>
                                <Fact label="X / Twitter handle">
                                    {user?.twitter_handle
                                        ? `@${user.twitter_handle.replace("@", "")}`
                                        : null}
                                </Fact>
                            </dl>
                        )}
                    </section>

                    <ChangePasswordModal
                        isOpen={showPasswordModal}
                        onClose={() => setShowPasswordModal(false)}
                        onSuccess={handlePasswordChangeSuccess}
                    />
                </div>
            </div>
        </>
    );
};

export default Profile;
